import type { ManualChapter } from '../types';

/** Rozdział instrukcji: Pierwsze kroki (28.10.2026, S-28.10-02). */
export const chapter: ManualChapter = {
  key: 'start',
  title: 'Pierwsze kroki',
  audience: 'Dla każdego pracownika, który zaczyna pracę w aplikacji — niezależnie od działu i roli.',
  intro:
    'Ten rozdział pokazuje, jak się zalogować, jak zbudowane jest okno aplikacji, gdzie szukać powiadomień i ustawień własnego konta oraz jak działają listy i filtry. Opisy pracy poszczególnych działów są w kolejnych rozdziałach.',
  sections: [
    {
      id: 'zasada-naczelna',
      title: 'Zasada naczelna: system przygotowuje, człowiek zatwierdza',
      blocks: [
        {
          kind: 'text',
          text: 'Aplikacja zbiera zlecenia z TMS, dane floty z eco-trucka i dane z kartotek, liczy podpowiedzi i ostrzeżenia, ale decyzje zostają po Twojej stronie. Obsadzenie pojazdu, przekazanie zlecenia do Trade, wystawienie oferty na giełdę czy zapis zlecenia do TMS zawsze wymagają jawnej akcji człowieka.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'System przygotowuje: importuje zlecenia i kartoteki z TMS, liczy dolot, „zdąży” i „dowiezie”, podpowiada pojazdy i przewoźników, przygotowuje szkice wiadomości i plików, pokazuje ostrzeżenia i powiadomienia.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Człowiek zatwierdza: obsadza, przekazuje, publikuje na giełdzie, wysyła wiadomości ze swojej poczty i zatwierdza zapis do TMS. Nic nie wysyła się samo — do TMS (VIA) trafia wyłącznie plik i tylko po Twojej akcji.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Nieczytelne albo nieznane dane zostają pustym polem z wyjaśnieniem — system nie zgaduje. Brak liczby (np. „—” przy koszcie) to brak danych, nie zero.',
        },
      ],
    },
    {
      id: 'logowanie',
      title: 'Logowanie i wylogowanie',
      blocks: [
        {
          kind: 'text',
          text: 'Konto zakłada administrator — dostajesz od niego adres e-mail konta i hasło startowe. Każdy pracownik ma własne konto; nie korzystaj z cudzego, bo każda zmiana zapisuje się w historii z nazwą konta.',
        },
        {
          kind: 'task',
          title: 'Jak się zalogować',
          steps: [
            'Otwórz aplikację w przeglądarce — zobaczysz okno „Marathon — Bank zleceń i planowanie transportu”.',
            'Wpisz adres w polu „E-mail” i hasło w polu „Hasło”.',
            'Kliknij „Zaloguj się”.',
          ],
          outcome:
            'Po zalogowaniu otwiera się pulpit. Gdy logowanie się nie uda, sprawdź wielkość liter w haśle; zapomniane hasło zmienia administrator.',
        },
        {
          kind: 'task',
          title: 'Jak się wylogować',
          steps: [
            'Kliknij swoją nazwę w prawym górnym rogu.',
            'Wybierz „Wyloguj”.',
          ],
          outcome: 'Na wspólnym komputerze zawsze się wylogowuj, zanim odejdziesz od stanowiska.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Na środowisku akceptacyjnym nad formularzem logowania i nad ekranami stoi pasek „Środowisko akceptacyjne — dane z kopii produkcji, integracje wyłączone”. Tam można spokojnie ćwiczyć: nic nie trafia do TMS ani na giełdy.',
        },
      ],
    },
    {
      id: 'okno-aplikacji',
      title: 'Okno aplikacji: menu, pasek górny, ekran roboczy',
      blocks: [
        {
          kind: 'text',
          text: 'Po lewej stronie jest menu boczne, u góry niebieski pasek z tytułem bieżącego ekranu, a pośrodku ekran roboczy. Menu pokazuje tylko te pozycje, do których Twoja rola ma uprawnienie — planista nie zobaczy ekranów Trade, a trader ekranów floty. To normalne, a nie błąd.',
        },
        {
          kind: 'image',
          image: { shot: 'menu' },
          caption: 'Menu boczne — pozycje zależą od roli i działu.',
        },
        {
          kind: 'text',
          text: 'Gdy okno przeglądarki jest wąskie (np. na laptopie albo przy oknie na pół ekranu), menu boczne samo zwija się do ikon, żeby zostawić więcej miejsca na ekran roboczy. Najedź na ikonę, żeby zobaczyć nazwę pozycji. Całe menu rozwiniesz przyciskiem z trzema kreskami w lewym górnym rogu („Otwórz menu”). Po poszerzeniu okna menu wraca samo — chyba że wcześniej zwinąłeś je sam.',
        },
        {
          kind: 'text',
          text: 'W wąskim oknie listy zleceń łączą niektóre kolumny w pary, jedna pod drugą — np. „Spółka / VAT”. Treść jest ta sama, a strona nie przewija się w bok.',
        },
        {
          kind: 'text',
          when: { on: ['mockupLook'] },
          text: 'Nowy wygląd aplikacji: sekcje ekranów stoją w jasnych ramkach z niebieskim znacznikiem przy tytule, nagłówki tabel są szare, stany zleceń to kolorowe znaczniki, a wybrana pozycja menu ma jasnoniebieskie tło. Zmienia się tylko wygląd — dane, przyciski i ich działanie są takie same jak wcześniej. Długie teksty zawijają się do kolejnego wiersza, więc nic nie jest ucięte.',
        },
        {
          kind: 'terms',
          title: 'Grupy menu',
          items: [
            { term: 'Zlecenia', meaning: 'Bank zleceń z TMS i prezleceń — wspólny dla FTL i Trade; obok pulpity działów, prezlecenia i zlecenia z maila.' },
            { term: 'Flota', meaning: '„Samochody” i „Harmonogram floty” — dla planowania FTL.' },
            { term: 'Przewoźnicy', meaning: 'Kartoteka przewoźników — dla Trade.' },
            { term: 'Dane podstawowe', meaning: 'Kontrahenci, kierowcy i naczepy.' },
            { term: 'Giełdy', meaning: '„Kolejka publikacji” ofert na Timocom i Trans.eu — dla Trade.' },
            { term: 'Administracja', meaning: 'Konta, spółki, poświadczenia, dziennik działań i parametry — dla administratorów.' },
          ],
        },
        {
          kind: 'terms',
          title: 'Pasek górny',
          items: [
            { term: 'Twoja nazwa (prawy górny róg)', meaning: 'Otwiera menu użytkownika: profil, preferencje, wylogowanie.' },
            {
              term: '„Bezczynne: N”',
              meaning: 'Zlecenia, którymi nikt się nie zajął w czasie wyznaczonym dla ich stanu. Kliknięcie otwiera listę tych zleceń.',
            },
            { term: 'Dzwonek', meaning: 'Powiadomienia działów FTL i Trade — liczba na dzwonku to nieprzeczytane.' },
            {
              term: '„Wersja: …”',
              meaning: 'Tylko na środowisku akceptacyjnym: pokazuje, czy pracujesz w wersji obecnej (jak na produkcji), czy nowej (do akceptacji).',
            },
          ],
        },
      ],
    },
    {
      id: 'pulpit',
      title: 'Pulpit',
      blocks: [
        {
          kind: 'text',
          text: 'Pulpit to pierwszy ekran po zalogowaniu. Kafelki pokazują liczby zleceń i pojazdów wymagających uwagi; kliknięcie kafelka otwiera listę z gotowym filtrem, więc liczba na kafelku i liczba wierszy na liście mówią o tym samym.',
        },
        {
          kind: 'image',
          image: { shot: 'dashboard' },
          caption: 'Pulpit — kafelki prowadzą do list z ustawionym filtrem.',
        },
        {
          kind: 'terms',
          title: 'Sekcje pulpitu',
          items: [
            { term: 'Zlecenia', meaning: '„Gotowe do planowania”, „Wstępnie planowane”, „Zagrożone”, „Po terminie ważności”, „Bezczynne” i „Straciły pojazd”.' },
            { term: 'Flota', meaning: '„Pojazdy pod zlecenie”, dostępne, „Poza eksploatacją” i „Bez kierowcy” — widoczne dla planowania FTL.' },
            { term: 'Realizacja zewnętrzna', meaning: 'Zlecenia „Obsadzone przewoźnikiem” i liczba przewoźników w kartotece. Pojazdów przewoźników nie ma w naszej flocie — to normalne.' },
            { term: 'Dane do uzupełnienia', meaning: 'Miejsca, w których kartoteki w TMS nie zgadzają się ze zleceniami (np. „Pojazdy bez spółki”). To sygnał, co poprawić w TMS, a nie zadanie planisty.' },
          ],
        },
        {
          kind: 'text',
          text: 'Działy mają też własne pulpity w menu (np. „Pulpit planisty”, „Pulpit Trade”) — opisują je rozdziały działów.',
        },
      ],
    },
    {
      id: 'menu-uzytkownika',
      title: 'Menu użytkownika i własne konto',
      blocks: [
        {
          kind: 'text',
          text: 'Menu użytkownika otwierasz, klikając swoją nazwę w prawym górnym rogu. Pod nazwą widać Twoją rolę, np. „Planista FTL (opiekun pojazdów)” albo „Trader”.',
        },
        {
          kind: 'text',
          when: { off: ['ownProfile'] },
          text: 'Pasek pokazuje nazwę konta (login). Imię i nazwisko wpisuje administrator w ustawieniach konta.',
        },
        {
          kind: 'task',
          when: { on: ['ownProfile'] },
          title: 'Jak wpisać swoje imię i nazwisko',
          intro: 'Imię i nazwisko pokazujemy zamiast loginu — w pasku, w podpisie zapytań do przewoźników i jako osobę kontaktową w ofercie na Timocom.',
          steps: [
            'Kliknij swoją nazwę w prawym górnym rogu.',
            'Wybierz „Mój profil”.',
            'Wpisz „Imię” i „Nazwisko” i kliknij „Zapisz”.',
          ],
          outcome: 'Pojawi się komunikat „Zapisano imię i nazwisko”, a pasek pokaże je zamiast loginu. Zmianę widać w dzienniku działań (bez treści pól).',
        },
        {
          kind: 'task',
          title: 'Jak wskazać, że jesteś opiekunem FTL',
          intro: 'Dotyczy osób planujących FTL. Opiekun to osoba przypisana do pojazdów w TMS.',
          steps: [
            'Kliknij swoją nazwę w prawym górnym rogu.',
            'W polu „Jestem opiekunem FTL” wybierz siebie z listy (albo „— nie ustawiono —”).',
          ],
          outcome:
            'Z tego wyboru korzysta przełącznik „Tylko moje pojazdy” na liście floty, w harmonogramie floty i w rekomendacjach. Planista dodaje i usuwa warianty tylko przy autach, których jest opiekunem. To zawężenie widoku, nie uprawnienie — jednym kliknięciem wracasz do całej floty.',
        },
        {
          kind: 'terms',
          title: 'Pozostałe pozycje menu użytkownika',
          items: [
            {
              term: '„Wersja: obecna (jak na produkcji)” / „Wersja: nowa (do akceptacji)”',
              meaning: 'Tylko na środowisku akceptacyjnym. Wersja obecna działa tak jak produkcja: funkcje już wydane są włączone, a nowe, czekające na decyzję — wyłączone. W wersji nowej widzisz także te nowe funkcje. Po zmianie ekran wczytuje się od nowa.',
            },
            {
              term: '„Przewodnik po zmianach”',
              meaning: 'Włącza i wyłącza panel prowadzący przez nowe funkcje.',
              when: { on: ['guideOverlay'] },
            },
            {
              term: '„Co nowego”',
              meaning: 'Lista przewodników po funkcjach widocznych w Twojej wersji aplikacji, ze znacznikiem „nowe” albo „obejrzany”.',
              when: { on: ['guideOverlay'] },
            },
            { term: '„Wyloguj”', meaning: 'Kończy pracę na tym koncie.' },
          ],
        },
      ],
    },
    {
      id: 'powiadomienia',
      title: 'Powiadomienia (dzwonek)',
      blocks: [
        {
          kind: 'text',
          text: 'Dzwonek w pasku górnym zbiera powiadomienia działów FTL i Trade z historii zmian zleceń: zlecenie przeszło do Twojego działu, zakończyło się, pojawił się alert o zmianie w TMS, przeplanowaniu albo braku auta przed załadunkiem. Własnych zmian nie dostajesz.',
        },
        {
          kind: 'task',
          title: 'Jak przejrzeć powiadomienia i wybrać obserwowane działy',
          steps: [
            'Kliknij dzwonek w pasku górnym.',
            'W „Obserwowane działy” zaznacz FTL, Trade albo oba — pusta lista oznacza wszystkie.',
            'Kliknij numer zlecenia, żeby otworzyć jego kartę.',
            'Gdy przejrzysz listę, kliknij „Oznacz wszystkie jako przeczytane”.',
          ],
        },
        {
          kind: 'text',
          when: { off: ['notificationsPage'] },
          text: 'Dzwonek pokazuje powiadomienia z ostatnich 14 dni.',
        },
        {
          kind: 'text',
          when: { on: ['notificationReadState'] },
          text: 'Każde powiadomienie możesz oznaczyć jako przeczytane osobno — kliknięciem numeru zlecenia albo „Oznacz jako przeczytane”. Licznik na dzwonku liczy nieprzeczytane pozycje.',
        },
        {
          kind: 'terms',
          title: 'Znaczniki w powiadomieniach',
          items: [
            {
              term: '„nieaktualne”',
              meaning: 'Warunek alertu już nie trwa (potwierdzony, rozpatrzony albo zlecenie ma już wykonawcę) — powiadomienie zostaje w historii, ale nie jest już wyróżnione jako ważne. Ten znacznik nie zależy od tego, czy pozycja jest przeczytana.',
              when: { on: ['notificationReadState'] },
            },
            {
              term: 'Powiadomienia imienne',
              meaning: 'Dotyczą Ciebie osobiście: zlecenie zdjęte z pojazdu, którego jesteś opiekunem FTL, odpięte albo przekazane do Trade; dla Team Leadera — zgłoszone ryzyko i przekazania między FTL a Trade; dla autora przekazania do Trade — akceptacja przewoźnika, sprzedaż, zwrot; dla autora wariantu — zlecenie z prezlecenia jest już w TMS.',
              when: { on: ['namedNotifications'] },
            },
          ],
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Powiadomienie to odczyt historii zlecenia — nic nie zmienia w zleceniu. Decyzję podejmujesz na karcie zlecenia.',
        },
      ],
    },
    {
      id: 'strona-powiadomien',
      title: 'Strona „Powiadomienia”',
      when: { on: ['notificationsPage'] },
      menu: ['notifications'],
      blocks: [
        {
          kind: 'text',
          text: 'Strona „Powiadomienia” pokazuje te same powiadomienia co dzwonek, ale z dłuższego okresu i z filtrami. Otwierasz ją z menu albo odnośnikiem „Wszystkie powiadomienia — historia i filtry” w dzwonku.',
        },
        {
          kind: 'task',
          title: 'Jak znaleźć starsze powiadomienie',
          steps: [
            'Otwórz „Powiadomienia” w menu.',
            'W polu „Okres” wybierz 7, 14, 30 albo 90 dni.',
            'Zawęź listę polami „Dział”, „Rodzaj” i „Stan” (przeczytane / nieprzeczytane).',
            'Kliknij numer w kolumnie „Zlecenie”, żeby przejść do karty zlecenia.',
          ],
        },
        {
          kind: 'list',
          when: { on: ['notificationReadState'] },
          items: [
            'Filtr „Aktualność” pokazuje aktualne, nieaktualne albo wszystkie powiadomienia.',
            'Przy każdej pozycji jest „Oznacz jako przeczytane”.',
          ],
        },
        {
          kind: 'image',
          when: { on: ['notificationReadState'] },
          image: { guide: 'notificationReadState', step: 1 },
          caption: 'Znacznik „nieaktualne” i filtr „Aktualność”.',
        },
        {
          kind: 'text',
          when: { on: ['namedNotifications'] },
          text: 'Zaznacz „Tylko moje”, żeby zobaczyć wyłącznie powiadomienia imienne. W polu „Rodzaj” wybierzesz konkretne zdarzenie, np. „Zlecenie zdjęte z Twojego pojazdu”.',
        },
        {
          kind: 'image',
          when: { on: ['namedNotifications'] },
          image: { guide: 'namedNotifications', step: 1 },
          caption: 'Powiadomienia imienne opiekuna pojazdu.',
        },
      ],
    },
    {
      id: 'listy-i-filtry',
      title: 'Listy i filtry',
      blocks: [
        {
          kind: 'text',
          text: 'Większość ekranów to listy: zlecenia, pojazdy, kierowcy, przewoźnicy. Nad listą są filtry stałe, a kolejne dodajesz przyciskiem „Dodaj filtr”. Kliknięcie nagłówka kolumny sortuje listę, a liczbę wierszy na stronie ustawiasz pod tabelą.',
        },
        {
          kind: 'task',
          title: 'Jak zawęzić listę filtrem',
          steps: [
            'Kliknij „Dodaj filtr” nad listą.',
            'Wybierz filtr z listy i ustaw jego wartość.',
            'Aby wrócić do całej listy, kliknij „Usuń wszystkie filtry” w menu „Dodaj filtr” albo krzyżyk przy filtrze.',
          ],
        },
        {
          kind: 'text',
          when: { off: ['autoListFilters'] },
          text: 'Przeglądarka pamięta ostatnie filtry listy wspólnie dla wszystkich kont na tym komputerze, a wylogowanie je czyści. Ustawienie, do którego często wracasz, zapiszesz pozycją „Zapisz obecne filtry” w menu „Dodaj filtr”.',
        },
        {
          kind: 'text',
          when: { on: ['autoListFilters'] },
          text: 'Każda lista sama pamięta ostatnie filtry, sortowanie i liczbę wierszy na stronie — osobno dla Twojego konta, także po przeładowaniu strony i ponownym zalogowaniu. Na wspólnym komputerze kolejna osoba nie dostaje Twoich filtrów. Pozycji „Zapisz obecne filtry” nie ma, bo nie jest potrzebna; „Widoki” na liście zleceń działają jak dotąd.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Gdy lista wydaje się pusta albo krótsza niż zwykle, najpierw sprawdź ustawione filtry — najczęściej to one ukrywają wiersze.',
        },
      ],
    },
    {
      id: 'role-i-uprawnienia',
      title: 'Role, uprawnienia i konto gościa',
      blocks: [
        {
          kind: 'text',
          text: 'Rola konta decyduje, które ekrany i przyciski widzisz. Przycisk, którego nie widzisz, jest ukryty, bo Twoja rola nie ma do niego uprawnienia; o dostępie i tak rozstrzyga serwer, więc ukrycie przycisku niczego nie zmienia w danych.',
        },
        {
          kind: 'terms',
          title: 'Najczęstsze role',
          items: [
            { term: 'Planista FTL (opiekun pojazdów)', meaning: 'Planuje flotę: rekomendacje, warianty, karta pojazdu. Nie obsadza finalnie.' },
            { term: 'Planista główny FTL (master)', meaning: 'Team Leader FTL: obsadza pojazdy, rozpatruje wnioski planistów.' },
            { term: 'Trader / Team Leader Trade', meaning: 'Zlecenia Trade, giełdy, przewoźnicy.' },
            { term: 'Administrator / Administrator spółki', meaning: 'Konta, spółki, parametry i ustawienia.' },
            { term: 'Gość', meaning: 'Tylko ogląda bank zleceń i karty — żadnej zmiany danych nie zapisze.' },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Gdy próbujesz zrobić coś, do czego rola nie ma uprawnienia, zobaczysz komunikat o braku uprawnień — nie zostaniesz wylogowany. Jeśli uważasz, że uprawnienie jest potrzebne, zgłoś to administratorowi.',
        },
      ],
    },
    {
      id: 'przewodnik-po-zmianach',
      title: 'Przewodnik po zmianach i „Co nowego”',
      when: { on: ['guideOverlay'] },
      blocks: [
        {
          kind: 'text',
          text: 'Przewodnik po zmianach to mały panel, który prowadzi krok po kroku przez nowe funkcje: co funkcja robi, gdzie ją znaleźć i co kliknąć. Przy każdym kroku jest slajd z ekranu i przycisk „Pokaż na ekranie”, który podświetla właściwe miejsce. Na ekranie z nową funkcją panel sam proponuje „Nowe: … — pokaż, jak działa”, ale niczego nie wymusza.',
        },
        {
          kind: 'task',
          title: 'Jak obejrzeć przewodnik po nowej funkcji',
          steps: [
            'Kliknij swoją nazwę w prawym górnym rogu i wybierz „Co nowego”.',
            'Przy wybranej funkcji kliknij „Pokaż przewodnik”.',
            'Czytaj krok w panelu i klikaj „Pokaż na ekranie”, a potem „Dalej”.',
            'Kliknij miniaturę slajdu, żeby go powiększyć; Esc zamyka podgląd.',
          ],
          outcome: 'Przewodnik zostaje oznaczony jako obejrzany. „–” (albo Esc) zwija panel do paska u dołu ekranu, a kliknięcie nazwy przewodnika na pasku rozwija go z powrotem.',
        },
        {
          kind: 'task',
          title: 'Jak przesunąć panel albo zwinięty pasek przewodnika',
          intro: 'Gdy panel zasłania to, nad czym pracujesz, odsuń go w inne miejsce ekranu.',
          steps: [
            'Rozwinięty panel chwyć za pasek tytułu i przeciągnij myszą.',
            'Zwinięty pasek chwyć za uchwyt z kropkami po jego lewej stronie i przeciągnij.',
            'Zamiast myszy możesz kliknąć uchwyt (albo pasek tytułu rozwiniętego panelu) i przesuwać go strzałkami; klawisz Home odsyła go z powrotem do prawego dolnego rogu.',
          ],
          outcome: 'Przeglądarka pamięta położenie panelu. Po zmniejszeniu okna panel sam wraca w granice ekranu.',
        },
        {
          kind: 'task',
          title: 'Jak wyłączyć albo włączyć panel przewodnika',
          steps: [
            'Kliknij swoją nazwę w prawym górnym rogu.',
            'Wybierz „Przewodnik po zmianach”.',
          ],
          outcome: 'Wybór pamięta przeglądarka osobno dla każdego konta.',
        },
        {
          kind: 'image',
          image: { guide: 'guideOverlay', step: 2 },
          caption: 'Lista „Co nowego” z przewodnikami po funkcjach.',
        },
      ],
    },
    {
      id: 'instrukcja',
      title: 'Instrukcja w aplikacji',
      when: { on: ['userManual'] },
      menu: ['manual'],
      blocks: [
        {
          kind: 'text',
          text: 'Pozycja „Instrukcja” w menu otwiera tę instrukcję. Rozdziały odpowiadają działom i rolom: Pierwsze kroki, Planista FTL, Team Leader FTL, Trade, CS, Administracja i Administrator. Na start otwiera się rozdział Twojej roli — na liście rozdziałów ma znacznik „Twoja rola”.',
        },
        {
          kind: 'task',
          title: 'Jak znaleźć opis funkcji',
          steps: [
            'Otwórz „Instrukcja” w menu.',
            'Wpisz słowo w polu „Szukaj w instrukcji”, np. „wariant” albo „giełda” — polskie znaki i wielkość liter nie mają znaczenia.',
            'Kliknij wynik, żeby przejść do sekcji; inny rozdział wybierzesz z listy rozdziałów.',
          ],
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Treść instrukcji zależy od funkcji włączonych dla Twojego konta: opisy funkcji, których nie masz, są ukryte, a w ich miejscu stoi opis tego, jak działa ekran dziś. Zrzuty ekranów pochodzą z danych pokazowych.',
        },
        {
          kind: 'image',
          image: { shot: 'manual' },
          caption: 'Instrukcja z listą rozdziałów i wyszukiwarką.',
        },
      ],
    },
  ],
};
